import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CaretLeft,
  Microphone,
  MicrophoneSlash,
  VideoCamera,
  VideoCameraSlash,
  Hand,
  DoorOpen,
  Users,
  MusicNote,
  ChatCircle,
  SealCheck,
  PaperPlaneTilt,
  Gear,
  SpeakerHigh,
} from "@phosphor-icons/react";
import Sidebar from "@/components/Sidebar";
import BottomNav from "@/components/BottomNav";
import { getRoom, getRoomMembers, currentUser } from "@/data/mockData";

const initialChat = [
  { id: "c1", slug: "mar-quintero", text: "buenas, entrando al canal 👋", time: "14:02" },
  { id: "c2", slug: "yuki-nakamura", text: "bienvenida. suave música hoy", time: "14:03" },
  { id: "c3", slug: "theo-volkov", text: "vuelvo en 10, hidratándome", time: "14:21" },
  { id: "c4", slug: "sol-cabrera", text: "referencia de color para quien quiera: #2A1810", time: "14:34" },
];

export default function RoomDetail() {
  const { slug } = useParams();
  const room = getRoom(slug);
  const members = getRoomMembers(room);
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [chat, setChat] = useState(initialChat);
  const [draft, setDraft] = useState("");

  const sendChat = () => {
    const text = draft.trim();
    if (!text) return;
    setChat([
      ...chat,
      { id: `u${Date.now()}`, slug: "tu", text, time: "ahora" },
    ]);
    setDraft("");
  };

  const getMember = (s) => members.find((m) => m.slug === s);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F4F5]">
      <Sidebar />
      <main className="md:pl-[240px] lg:pl-[260px] pb-20 md:pb-0">
        <div className="max-w-[1600px] mx-auto" data-testid="room-detail">
          {/* Top header with ambient */}
          <header className="relative overflow-hidden border-b border-[#27272A]">
            <div className="absolute inset-0">
              <img
                src={room.ambient}
                alt=""
                className="w-full h-full object-cover grayscale opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#050505]" />
            </div>
            <div className="relative flex items-start justify-between gap-4 p-5 md:p-6 flex-wrap">
              <div className="flex items-start gap-4">
                <Link
                  to="/salas"
                  data-testid="back-rooms"
                  className="h-10 w-10 border border-[#27272A] hover:border-[#FF3E00] inline-flex items-center justify-center shrink-0"
                >
                  <CaretLeft size={16} />
                </Link>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] bg-[#00FF66]/10 border border-[#00FF66] text-[#00FF66] px-2 py-1">
                      <span className="h-1.5 w-1.5 bg-[#00FF66] live-dot" />
                      ABIERTO 24/7
                    </span>
                    <span
                      className="font-mono text-[10px] tracking-[0.22em]"
                      style={{ color: room.color }}
                    >
                      {room.category}
                    </span>
                  </div>
                  <h1 className="font-display text-3xl md:text-5xl font-black tracking-tighter uppercase leading-none">
                    {room.name}
                  </h1>
                  <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-3">
                    // {room.mood.toUpperCase()} · ▸ {room.activity}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wider bg-black/70 border border-[#27272A] px-3 py-2">
                  <Users size={12} weight="fill" className="text-[#FF3E00]" />
                  {room.participants}/{room.capacity}
                </span>
                <Link
                  to="/salas"
                  data-testid="leave-room"
                  className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] border border-[#FF0033] text-[#FF0033] hover:bg-[#FF0033] hover:text-white px-3 py-2 inline-flex items-center gap-2"
                >
                  <DoorOpen size={13} /> Salir
                </Link>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px]">
            {/* Room floor */}
            <section className="p-4 md:p-6 border-r border-[#27272A]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold uppercase tracking-tight">
                  En la sala ahora
                </h2>
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                  MIC MUTEADO POR DEFECTO
                </span>
              </div>

              {/* Grid of participants */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {/* You */}
                <ParticipantTile
                  member={{ ...currentUser, name: "Tú", verified: false }}
                  isYou
                  micOn={micOn}
                  camOn={camOn}
                  handRaised={handRaised}
                />
                {members.map((m, i) => (
                  <ParticipantTile
                    key={m.slug}
                    member={m}
                    talking={i === 0}
                    micOn={i !== 1}
                    camOn={i === 0}
                  />
                ))}
                {/* Empty slots */}
                {Array.from({
                  length: Math.max(0, Math.min(4, room.participants - members.length - 1)),
                }).map((_, i) => (
                  <div
                    key={`e-${i}`}
                    className="relative aspect-[4/5] border border-dashed border-[#27272A] bg-[#0a0a0a] flex flex-col items-center justify-center gap-2 text-[#525252]"
                  >
                    <Users size={20} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em]">
                      anónimos · {Math.max(0, room.participants - members.length - 1)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Ambient player */}
              <div className="mt-8 border border-[#27272A] bg-[#0a0a0a] p-4 flex items-center gap-4">
                <div className="h-12 w-12 border border-[#FF3E00] text-[#FF3E00] inline-flex items-center justify-center shrink-0">
                  <MusicNote size={18} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                    AMBIENTE
                  </div>
                  <div className="font-display font-bold truncate">
                    Cascadas · Nils Frahm
                  </div>
                  {/* Waveform */}
                  <div className="flex items-end gap-0.5 mt-2 h-4">
                    {Array.from({ length: 48 }).map((_, i) => (
                      <span
                        key={i}
                        className="flex-1 bg-[#FF3E00]/70 live-dot"
                        style={{
                          animationDelay: `${(i % 12) * 0.1}s`,
                          height: `${40 + ((i * 13) % 60)}%`,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <button className="h-10 w-10 border border-[#27272A] hover:border-[#F4F4F5] inline-flex items-center justify-center shrink-0">
                  <SpeakerHigh size={15} />
                </button>
              </div>

              {/* Control bar */}
              <div
                data-testid="room-controls"
                className="mt-4 flex items-center justify-center gap-2 p-4 border border-[#27272A] bg-[#0a0a0a]"
              >
                <ControlBtn
                  active={micOn}
                  onClick={() => setMicOn((v) => !v)}
                  label={micOn ? "Mic" : "Muteado"}
                  icon={micOn ? Microphone : MicrophoneSlash}
                  activeColor="#00FF66"
                  testid="toggle-mic"
                />
                <ControlBtn
                  active={camOn}
                  onClick={() => setCamOn((v) => !v)}
                  label={camOn ? "Cámara" : "Cámara off"}
                  icon={camOn ? VideoCamera : VideoCameraSlash}
                  activeColor="#00FF66"
                  testid="toggle-cam"
                />
                <ControlBtn
                  active={handRaised}
                  onClick={() => setHandRaised((v) => !v)}
                  label={handRaised ? "Mano alzada" : "Levantar"}
                  icon={Hand}
                  activeColor="#FF3E00"
                  testid="toggle-hand"
                />
                <button
                  aria-label="Ajustes"
                  className="h-11 w-11 border border-[#27272A] text-[#A1A1AA] hover:border-[#F4F4F5] hover:text-white inline-flex items-center justify-center"
                >
                  <Gear size={15} />
                </button>
                <Link
                  to="/salas"
                  className="btn-brutal ml-2 font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF0033] text-white px-4 py-3 inline-flex items-center gap-2"
                >
                  <DoorOpen size={13} /> Salir
                </Link>
              </div>
            </section>

            {/* Chat sidebar */}
            <aside className="flex flex-col min-h-[600px] max-h-[calc(100vh-200px)]">
              <header className="flex items-center justify-between p-4 border-b border-[#27272A]">
                <div className="flex items-center gap-2">
                  <ChatCircle size={14} className="text-[#FF3E00]" />
                  <h3 className="font-display font-bold uppercase tracking-tight text-sm">
                    Chat del canal
                  </h3>
                </div>
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                  {chat.length} MSG
                </span>
              </header>

              <div className="flex-1 overflow-auto p-3 space-y-3 bg-[#050505]">
                {chat.map((m) => {
                  const isMe = m.slug === "tu";
                  const author = isMe
                    ? { ...currentUser, name: "Tú" }
                    : getMember(m.slug) || members[0];
                  return (
                    <div key={m.id} className="flex gap-2 items-start fade-up">
                      <img
                        src={author.avatar}
                        alt={author.name}
                        className="h-7 w-7 object-cover border border-[#27272A] shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            className={`font-display font-bold text-xs ${
                              isMe ? "text-[#FF3E00]" : ""
                            }`}
                          >
                            {author.name}
                          </span>
                          {author.verified ? (
                            <SealCheck size={10} weight="fill" className="text-[#FF3E00]" />
                          ) : null}
                          <span className="font-mono text-[9px] text-[#525252]">
                            {m.time}
                          </span>
                        </div>
                        <p className="text-[13px] text-[#E5E5E5] leading-snug">
                          {m.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#27272A] p-3 flex items-center gap-2">
                <input
                  data-testid="room-chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendChat()}
                  placeholder="Escribe al canal..."
                  className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-3 py-2 font-mono text-xs"
                />
                <button
                  data-testid="room-chat-send"
                  onClick={sendChat}
                  disabled={!draft.trim()}
                  className={`font-mono uppercase text-[10px] tracking-[0.2em] px-3 py-2 ${
                    draft.trim() ? "bg-[#FF3E00] text-white" : "bg-[#121212] text-[#525252]"
                  }`}
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

function ParticipantTile({ member, isYou, talking, micOn, camOn, handRaised }) {
  return (
    <div
      className={`relative aspect-[4/5] border overflow-hidden group ${
        talking ? "border-[#00FF66]" : isYou ? "border-[#FF3E00]" : "border-[#27272A]"
      }`}
    >
      <img
        src={member.avatar}
        alt={member.name}
        className={`absolute inset-0 w-full h-full object-cover ${
          camOn ? "" : "grayscale opacity-70"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />

      {/* Top row */}
      <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {isYou ? (
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-1.5 py-0.5">
              TÚ
            </span>
          ) : null}
          {talking ? (
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] bg-[#00FF66] text-black px-1.5 py-0.5 inline-flex items-center gap-1">
              <span className="h-1 w-1 bg-black live-dot" />
              HABLANDO
            </span>
          ) : null}
        </div>
        {handRaised ? (
          <span className="h-6 w-6 bg-[#FF3E00] text-white inline-flex items-center justify-center">
            <Hand size={12} weight="fill" />
          </span>
        ) : null}
      </div>

      {/* Mic mute indicator centered when cam off */}
      {!camOn ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-12 w-12 bg-black/60 backdrop-blur-sm border border-white/20 inline-flex items-center justify-center">
            {micOn ? (
              <Microphone size={18} weight="fill" className="text-[#00FF66]" />
            ) : (
              <MicrophoneSlash size={18} weight="fill" className="text-[#FF0033]" />
            )}
          </div>
        </div>
      ) : null}

      {/* Bottom */}
      <div className="absolute bottom-0 inset-x-0 p-2">
        <div className="font-display font-bold text-[12px] truncate flex items-center gap-1">
          {member.name}
          {member.verified ? (
            <SealCheck size={10} weight="fill" className="text-[#FF3E00]" />
          ) : null}
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <span className="font-mono text-[9px] text-[#A1A1AA] tracking-wider truncate">
            @{member.handle || member.slug}
          </span>
          {camOn && !micOn ? (
            <MicrophoneSlash size={11} weight="fill" className="text-[#FF0033] shrink-0" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ControlBtn({ active, onClick, label, icon: Icon, activeColor, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-3 border transition-colors ${
        active
          ? activeColor === "#FF3E00"
            ? "text-white"
            : "text-black"
          : "border-[#27272A] text-[#A1A1AA] hover:border-[#F4F4F5] hover:text-white"
      }`}
      style={
        active
          ? { background: activeColor, borderColor: activeColor }
          : undefined
      }
    >
      <Icon size={14} weight={active ? "fill" : "regular"} />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}
